/**
 * HCE: lightweight toast notifications.
 *
 * Exists because quick-add on the collection and featured-product grids gave no
 * feedback whatsoever. It dimmed the button, silently swallowed any error
 * (`.catch(function () {})`), and wrote the new count into a cart badge that
 * Shopify only renders when the cart is already non-empty -- so the very first
 * add of a session appeared to do nothing at all.
 *
 * The product page keeps Dawn's richer cart-notification (it shows the product
 * and offers View cart / Check out). This is for the grid case, where a full
 * panel would be too heavy for a one-tap action.
 *
 * Accessibility: one persistent live region, polite for success and assertive
 * for errors, so a screen reader announces the result without the toast having
 * to steal focus. Motion is skipped entirely under prefers-reduced-motion.
 */
(function () {
  'use strict';

  var CONTAINER_ID = 'HceToastRegion';
  var DEFAULT_MS = 4000;
  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function region() {
    var el = document.getElementById(CONTAINER_ID);
    if (el) return el;
    el = document.createElement('div');
    el.id = CONTAINER_ID;
    el.className = 'hce-toast-region';
    // The region itself is the live area and must exist in the DOM *before* the
    // message is inserted, otherwise assistive tech may not announce it.
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'false');
    document.body.appendChild(el);
    return el;
  }

  function dismiss(toast) {
    if (toast.dataset.leaving) return;
    toast.dataset.leaving = '1';
    if (reduceMotion) {
      toast.remove();
      return;
    }
    toast.classList.remove('hce-toast--in');
    toast.classList.add('hce-toast--out');
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      toast.remove();
    };
    toast.addEventListener('transitionend', finish);
    setTimeout(finish, 500);
  }

  /**
   * @param {string} message
   * @param {{variant?: 'success'|'error', duration?: number, action?: {label: string, href: string}}} [options]
   */
  function toast(message, options) {
    var opts = options || {};
    var variant = opts.variant === 'error' ? 'error' : 'success';
    var host = region();
    host.setAttribute('aria-live', variant === 'error' ? 'assertive' : 'polite');

    var el = document.createElement('div');
    el.className = 'hce-toast hce-toast--' + variant;
    el.setAttribute('role', variant === 'error' ? 'alert' : 'status');

    var icon = document.createElement('span');
    icon.className = 'hce-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML =
      variant === 'error'
        ? '<svg viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8.25" stroke="currentColor" stroke-width="1.5"/><path d="M10 6v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="10" cy="13.75" r="1" fill="currentColor"/></svg>'
        : '<svg viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8.25" stroke="currentColor" stroke-width="1.5"/><path d="M6.25 10.25 8.75 12.75 13.75 7.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var text = document.createElement('span');
    text.className = 'hce-toast__text';
    text.textContent = message;

    el.appendChild(icon);
    el.appendChild(text);

    if (opts.action && opts.action.href) {
      var action = document.createElement('a');
      action.className = 'hce-toast__action';
      action.href = opts.action.href;
      action.textContent = opts.action.label || 'View';
      el.appendChild(action);
    }

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'hce-toast__close';
    close.setAttribute('aria-label', 'Dismiss notification');
    close.innerHTML = '<span aria-hidden="true">&times;</span>';
    close.addEventListener('click', function () {
      dismiss(el);
    });
    el.appendChild(close);

    host.appendChild(el);

    if (!reduceMotion) {
      // Force layout so the entry transition has two distinct states to animate
      // between, rather than collapsing into a single frame.
      void el.offsetWidth;
      el.classList.add('hce-toast--in');
    }

    var timer = null;
    var start = function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        dismiss(el);
      }, opts.duration || DEFAULT_MS);
    };
    // Don't yank the message away while it is being read or interacted with.
    el.addEventListener('mouseenter', function () {
      clearTimeout(timer);
    });
    el.addEventListener('mouseleave', start);
    el.addEventListener('focusin', function () {
      clearTimeout(timer);
    });
    el.addEventListener('focusout', start);
    start();

    return el;
  }

  window.hceToast = toast;
})();
